import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import ImageWithFallback from '../components/ImageWithFallback';
import FeatureCard from '../components/FeatureCard';
import ImageLightbox from '../components/ImageLightbox';
import { martyrsApi, tributesApi } from '../services/api';
import { formatDateToGregorian } from '../utils/dateFormatter';
import { toYouTubeEmbed } from '../utils/youtube';
import { parsePlace } from '../utils/place';

const MartyrDetailPage = () => {
  const [martyr, setMartyr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [tributes, setTributes] = useState([]);
  const [tributeForm, setTributeForm] = useState({ visitor_name: '', message: '' });
  const [tributeStatus, setTributeStatus] = useState('');
  const { id } = useParams();

  const fetchMartyr = useCallback(async () => {
    try {
      const data = await martyrsApi.getById(id);
      setMartyr(data.martyr);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMartyr();
  }, [fetchMartyr]);

  useEffect(() => {
    tributesApi.getForMartyr(id).then((data) => setTributes(data.tributes || [])).catch(() => setTributes([]));
  }, [id]);

  const handleTributeSubmit = async (e) => {
    e.preventDefault();
    setTributeStatus('');
    try {
      await tributesApi.submit({
        martyr_id: parseInt(id, 10),
        visitor_name: tributeForm.visitor_name,
        message: tributeForm.message
      });
      setTributeForm({ visitor_name: '', message: '' });
      setTributeStatus('تم إرسال كلمتك وستظهر بعد مراجعة الإدارة');
    } catch (err) {
      setTributeStatus(err.message || 'تعذر إرسال الكلمة');
    }
  };

  if (loading) {
    return <Layout><div className="py-24 text-center">جاري تحميل سيرة الشهيد...</div></Layout>;
  }
  if (error || !martyr) {
    return <Layout><div className="py-24 text-center text-error-600">{error || 'لم يتم العثور على الشهيد'}</div></Layout>;
  }

  const place = parsePlace(martyr.place_of_martyrdom);

  return (
    <Layout>
      <Seo title={martyr.name_ar} description={martyr.bio || `سيرة الشهيد ${martyr.name_ar}`} image={martyr.image_url} />
      <section className="relative min-h-[70vh] overflow-hidden bg-stone-950 text-white">
        <ImageWithFallback src={martyr.image_url || '/default.png'} alt={martyr.name_ar} className="absolute inset-0 h-full w-full object-cover opacity-40" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/70 to-black/30" />
        <div className="relative container-responsive flex min-h-[70vh] flex-col justify-end pb-14 pt-28">
          <p className="text-xs tracking-[0.35em] text-gold-300">سيرة شهيد</p>
          <h1 className="mt-3 text-4xl font-extrabold md:text-6xl">{martyr.name_ar}</h1>
          {martyr.name_en && <p className="mt-2 text-xl text-white/70">{martyr.name_en}</p>}
          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <span className="rounded-full bg-white/10 px-4 py-2">{formatDateToGregorian(martyr.date_of_martyrdom)}</span>
            {place.state && <span className="rounded-full bg-white/10 px-4 py-2">{place.state}{place.area ? ` — ${place.area}` : ''}</span>}
            {martyr.occupation && <span className="rounded-full bg-gold-500/20 px-4 py-2 text-gold-200">{martyr.occupation}</span>}
          </div>
        </div>
      </section>

      <div className="container-responsive grid gap-6 py-12 md:grid-cols-2 xl:grid-cols-3">
        <FeatureCard to={`/biography?id=${martyr.id}`} icon="📖" title="السيرة الذاتية" description="اقرأ النبذة والمسيرة التعليمية والمهنية." />
        <FeatureCard to={`/timeline?id=${martyr.id}`} icon="⏳" title="الخط الزمني" description="محطات من الحياة حتى لحظة الاستشهاد." />
        <FeatureCard to={`/gallery/photos?id=${martyr.id}`} icon="▣" title="معرض الصور" description="صور الشهيد في بطاقات هادئة." />
        <FeatureCard to={`/gallery/videos?id=${martyr.id}`} icon="▶" title="الفيديوهات" description="مقاطع مرتبطة بهذا الشهيد." />
        <FeatureCard to={`/writings?id=${martyr.id}`} icon="📚" title="كتب عنه" description="مقالات وكتب أضيفت عن هذا الشهيد." />
        <FeatureCard to={`/poems?id=${martyr.id}`} icon="✎" title="القصائد" description="قصائد الرثاء الخاصة بهذا الشهيد." />
      </div>

      <div className="container-responsive grid gap-8 pb-16 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {martyr.bio && (
            <section id="bio" className="rounded-3xl bg-white p-8 shadow-soft dark:bg-stone-900">
              <h2 className="mb-4 text-2xl font-bold">السيرة الذاتية</h2>
              <p className="whitespace-pre-line text-lg leading-loose text-stone-700 dark:text-stone-300">{martyr.bio}</p>
            </section>
          )}

          {(martyr.university_name || martyr.education_level) && (
            <section className="rounded-3xl bg-white p-8 shadow-soft dark:bg-stone-900">
              <h2 className="mb-4 text-2xl font-bold">التعليم</h2>
              <p>{martyr.education_level}</p>
              {martyr.university_name && <p className="text-stone-600">{martyr.university_name} {martyr.faculty ? `— ${martyr.faculty}` : ''}</p>}
            </section>
          )}

          {martyr.photos?.length > 0 && (
            <section>
              <h2 className="mb-4 text-2xl font-bold">الصور</h2>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {martyr.photos.map((photo, index) => (
                  <button key={photo.id} type="button" className="overflow-hidden rounded-2xl" onClick={() => setLightboxIndex(index)}>
                    <ImageWithFallback src={photo.file_url} alt={photo.title} className="h-40 w-full object-cover" />
                  </button>
                ))}
              </div>
            </section>
          )}

          {(martyr.youtube_playlist || martyr.videos?.length > 0) && (
            <section>
              <h2 className="mb-4 text-2xl font-bold">الفيديو</h2>
              {martyr.youtube_playlist && (
                martyr.youtube_display_type === 'embed' ? (
                  <iframe title="playlist" className="mb-4 aspect-video w-full rounded-2xl" src={martyr.youtube_playlist.replace('playlist?list=', 'embed/videoseries?list=')} allowFullScreen />
                ) : (
                  <a className="mb-4 inline-block rounded-full bg-red-600 px-5 py-2 text-white" href={martyr.youtube_playlist} target="_blank" rel="noreferrer">قائمة التشغيل</a>
                )
              )}
              <div className="grid gap-4 md:grid-cols-2">
                {(martyr.videos || []).map((video) => (
                  <div key={video.id} className="overflow-hidden rounded-2xl bg-black">
                    {video.youtube_url ? (
                      <iframe title={video.title} src={toYouTubeEmbed(video.youtube_url)} className="aspect-video w-full" allowFullScreen loading="lazy" />
                    ) : (
                      <video src={video.file_url} className="aspect-video w-full" controls preload="metadata" />
                    )}
                    <p className="p-3 text-white">{video.title}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="space-y-6">
          {martyr.facebook_link && (
            <a href={martyr.facebook_link} target="_blank" rel="noreferrer" className="block rounded-3xl bg-blue-600 p-5 text-center text-white">صفحة الفيسبوك</a>
          )}
          <section className="rounded-3xl bg-white p-6 shadow-soft dark:bg-stone-900">
            <h2 className="mb-4 text-xl font-bold">قالوا عنه</h2>
            {tributes.length === 0 && <p className="text-stone-500">كن أول من يكتب.</p>}
            {tributes.map((tribute) => (
              <blockquote key={tribute.id} className="mb-4 border-r-2 border-gold-400 pr-3">
                <p>{tribute.message}</p>
                <footer className="mt-1 text-sm text-gold-700">{tribute.visitor_name}</footer>
              </blockquote>
            ))}
            <form onSubmit={handleTributeSubmit} className="mt-4 space-y-3">
              <input className="form-input" placeholder="اسمك" value={tributeForm.visitor_name} onChange={(e) => setTributeForm((p) => ({ ...p, visitor_name: e.target.value }))} required />
              <textarea className="form-input min-h-[90px]" placeholder="كلمتك" value={tributeForm.message} onChange={(e) => setTributeForm((p) => ({ ...p, message: e.target.value }))} required minLength={10} />
              <button type="submit" className="btn btn-primary w-full">إرسال</button>
              {tributeStatus && <p className="text-sm">{tributeStatus}</p>}
            </form>
          </section>
          <Link to="/martyrs" className="btn btn-outline w-full">العودة للأرشيف</Link>
        </aside>
      </div>

      <ImageLightbox
        images={martyr.photos || []}
        index={lightboxIndex}
        onChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />
    </Layout>
  );
};

export default MartyrDetailPage;
